import type RouterService from '@ember/routing/router-service';
import Service, { inject as service } from '@ember/service';
import environment from 'garaje/config/environment';
import type { Attributes, ComponentClass, FunctionComponent } from 'react';
import React from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { type ReactRichComponentsConfig } from 'reactRichComponents/config';

import type AuthzService from './authz';
import type CurrentAdminService from './current-admin';
import type FeatureConfigService from './feature-config';
import type ReactAnalytics from './react-analytics';
import type SplitioService from './splitio';
import type StateService from './state';

export default class ReactShadowDomRendererService extends Service {
  @service declare reactAnalytics: ReactAnalytics;
  @service declare router: RouterService;
  @service declare splitio: SplitioService;
  @service declare state: StateService;
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureConfig: FeatureConfigService;

  async render<P extends object>(
    component: FunctionComponent<P> | ComponentClass<P>,
    elementId: string,
    props?: Attributes & P,
  ): Promise<Root> {
    const hostElement = document.getElementById(elementId);
    if (!hostElement) {
      throw new Error(`Element with id ${elementId} was not found.`);
    }

    const customElement = document.createElement('x-envoy-shadow-dom');
    hostElement.appendChild(customElement);
    const shadowRoot = customElement.attachShadow({ mode: 'open' });

    await this.injectStyles(shadowRoot);

    const container = document.createElement('div');
    container.style.display = 'contents';
    shadowRoot.appendChild(container);

    const { reactRichComponentsConfig } = await import('reactRichComponents/config');
    const { ShadowDomContainerProvider } = await import('reactRichComponents/shadow-dom');
    this.configureReactRichComponents(reactRichComponentsConfig);

    const renderedComponent = React.createElement<P>(component, props, null);
    const wrappedComponent = React.createElement(ShadowDomContainerProvider, {
      rootNode: shadowRoot as unknown as HTMLElement,
      children: renderedComponent,
    });
    const root = createRoot(container);

    // Issue with React's inbuilt typing, so we need to force an any assignment here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
    root.render(wrappedComponent as any);

    return root;
  }

  rerender<P extends object>(
    component: FunctionComponent<P> | ComponentClass<P>,
    root: Root,
    props?: Attributes & P,
  ): void {
    const renderedComponent = React.createElement<P>(component, props, null);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
    root.render(renderedComponent as any);
  }

  unmount(root: Root): void {
    root.unmount();
  }

  private async injectStyles(shadowRoot: ShadowRoot): Promise<void> {
    try {
      // Load styles via module federation (exports CSS as string)
      const stylesModule = await import('reactRichComponents/shadow-dom-styles');

      if (stylesModule.default) {
        const styleElement = document.createElement('style');

        // this hack is required for tailwindv4 to work in the shadow dom at the time of this writing
        // https://github.com/tailwindlabs/tailwindcss/issues/15005
        styleElement.textContent = stylesModule.default.replace(
          '((-webkit-hyphens: none) and (not (margin-trim: inline))) or ((-moz-orient: inline) and (not (color:rgb(from red r g b))))',
          '(display: block)',
        );
        shadowRoot.appendChild(styleElement);
      }
    } catch (_: unknown) {
      this.reactAnalytics.trackMonitoring('RRC_SHADOW_STYLE_FETCH_FAILED');
    }
  }

  private configureReactRichComponents(reactRichComponentsConfig: ReactRichComponentsConfig): void {
    this.setEnvironment(reactRichComponentsConfig);

    reactRichComponentsConfig.analytics = this.reactAnalytics;
    reactRichComponentsConfig.splitClient = this.splitio.getSdk('company');
    reactRichComponentsConfig.serviceUrls.envoyWeb = '/a/visitors/';
    reactRichComponentsConfig.serviceUrls.federatedGraphQL = '/a/graphql_federated';
    reactRichComponentsConfig.googleMapsApiKey = (
      environment as unknown as { googleMapsApiKey: string }
    ).googleMapsApiKey;
    reactRichComponentsConfig.featureConfig = {
      canAccessMultiPartApprovals: this.state.features?.canAccessMultiPartApprovals ?? false,
      mapsEdit: this.featureConfig.isEnabled('maps.edit'),
      takeoverScreenGroups: this.featureConfig.isEnabled('screens'),
    };
    reactRichComponentsConfig.authzPermissions = this.authz.permissions;
    reactRichComponentsConfig.currentAdminRoles = this.currentAdmin.roleNames;
    reactRichComponentsConfig.navigate = (path: string) => {
      void this.router.transitionTo(path);
    };
  }

  private setEnvironment(reactRichComponentsConfig: ReactRichComponentsConfig): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const emberEnv = (environment as unknown as { environment: string }).environment;
    if (emberEnv === 'development') {
      reactRichComponentsConfig.setEnvironment('dev');
    } else if (emberEnv === 'production') {
      reactRichComponentsConfig.setEnvironment('production');
    } else if (emberEnv === 'eu') {
      reactRichComponentsConfig.setEnvironment('eu');
    } else {
      reactRichComponentsConfig.setEnvironment('staging');
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'react-shadow-dom-renderer': ReactShadowDomRendererService;
  }
}
